import React, { useState, useEffect } from 'react';
import ContentGuard from './ContentGuard';
import './TutorialAdmin.css';

const TutorialAdmin = ({ onBack }) => {
  const [tutorials, setTutorials] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [category, setCategory] = useState('Getting Started');
  const [thumbnail, setThumbnail] = useState('');
  const [duration, setDuration] = useState('');
  const [progress, setProgress] = useState(0);

  const categories = ['Getting Started', 'Market Analysis', 'Trading', 'Features', 'Account & Settings'];

  useEffect(() => {
    const savedTutorials = localStorage.getItem('stocks4u_tutorials');
    if (savedTutorials) {
      setTutorials(JSON.parse(savedTutorials));
    }
  }, []);

  const saveTutorials = (updatedTutorials) => {
    localStorage.setItem('stocks4u_tutorials', JSON.stringify(updatedTutorials));
    setTutorials(updatedTutorials);
  };

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setThumbnail(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoUrl(`/videos/${file.name}`);
      alert(`Video selected: ${file.name}\n\nIMPORTANT: To make this video playable, please move the file from your Desktop to the project folder: "public/videos/${file.name}"`);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title || !videoUrl || !duration) {
      alert('Title, Video URL, and Duration are required!');
      return;
    }
    const tutorialData = {
      id: isEditing ? editId : Date.now(),
      title, description, videoUrl, category, thumbnail, duration,
      progress: parseInt(progress) || 0,
      date: new Date().toISOString()
    };
    let updatedTutorials;
    if (isEditing) {
      updatedTutorials = tutorials.map(t => t.id === editId ? tutorialData : t);
    } else {
      updatedTutorials = [...tutorials, tutorialData];
    }
    saveTutorials(updatedTutorials);
    resetForm();
    setShowForm(false);
    alert(isEditing ? 'Tutorial updated successfully!' : 'Tutorial posted successfully!');
  };

  const handleEdit = (tutorial) => {
    setEditId(tutorial.id);
    setTitle(tutorial.title);
    setDescription(tutorial.description);
    setVideoUrl(tutorial.videoUrl || '');
    setCategory(tutorial.category || 'Getting Started');
    setThumbnail(tutorial.thumbnail || '');
    setDuration(tutorial.duration || '');
    setProgress(tutorial.progress || 0);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this tutorial?')) {
      const updatedTutorials = tutorials.filter(t => t.id !== id);
      saveTutorials(updatedTutorials);
    }
  };

  const resetForm = () => {
    setTitle(''); setDescription(''); setVideoUrl('');
    setCategory('Getting Started'); setThumbnail('');
    setDuration(''); setProgress(0); setIsEditing(false); setEditId(null);
  };

  const handleNewTutorial = () => {
    resetForm();
    setShowForm(true);
  };

  return (
    <ContentGuard section="tutorials">
      <div className="tut-admin-page">
        <header className="tut-admin-header">
          <div className="tut-admin-header-container">
            {onBack && (
              <button className="tut-admin-back-btn" onClick={onBack}>
                ← Back
              </button>
            )}
            <h1>Tutorial Video Admin Panel</h1>
            <button className="new-tut-btn" onClick={handleNewTutorial}>
              + New Tutorial
            </button>
          </div>
        </header>

        <main className="tut-admin-main">
          {showForm && (
            <section className="tut-form-section">
              <h2>{isEditing ? 'Edit Tutorial' : 'Create New Tutorial'}</h2>
              <form className="tut-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Title *</label>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter tutorial title" required />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select value={category} onChange={(e) => setCategory(e.target.value)}>
                      {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Video URL / Path *</label>
                    <input type="text" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="/videos/my-video.mp4 or YouTube link" required />
                  </div>
                  <div className="form-group">
                    <label>Choose Local Video</label>
                    <input type="file" accept="video/*" onChange={handleVideoUpload} className="file-input" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Duration * (e.g. 05:30)</label>
                    <input type="text" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="MM:SS" required />
                  </div>
                  <div className="form-group">
                    <label>Initial Progress (%)</label>
                    <input type="number" min="0" max="100" value={progress} onChange={(e) => setProgress(e.target.value)} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" rows="2" />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Thumbnail Image</label>
                    <input type="file" accept="image/*" onChange={handleThumbnailUpload} className="file-input" />
                    {thumbnail && (
                      <div className="image-preview">
                        <img src={thumbnail} alt="Preview" />
                        <button type="button" className="remove-image-btn" onClick={() => setThumbnail('')}>Remove</button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="submit" className="save-btn">{isEditing ? 'Update Tutorial' : 'Post Tutorial'}</button>
                </div>
              </form>
            </section>
          )}

          <section className="tut-list-section">
            <h2>All Tutorials ({tutorials.length})</h2>
            {tutorials.length === 0 ? (
              <div className="empty-state">
                <p>No tutorials yet. Click "New Tutorial" to create one!</p>
              </div>
            ) : (
              <div className="admin-tut-list">
                {tutorials.map(tut => (
                  <div key={tut.id} className="admin-tut-card">
                    <div className="admin-tut-info">
                      <div className="admin-tut-thumb">
                        {tut.thumbnail ? <img src={tut.thumbnail} alt={tut.title} /> : <div className="thumb-placeholder">Video</div>}
                      </div>
                      <div className="admin-tut-details">
                        <h3>{tut.title}</h3>
                        <p>{tut.description}</p>
                        <div className="admin-tut-meta">
                          <span className="admin-tut-category">{tut.category}</span>
                          <span className="admin-tut-duration">{tut.duration}</span>
                          <span className="admin-tut-progress">{tut.progress}% Start</span>
                        </div>
                      </div>
                    </div>
                    <div className="admin-tut-actions">
                      <button className="edit-btn" onClick={() => handleEdit(tut)}>Edit</button>
                      <button className="delete-btn" onClick={() => handleDelete(tut.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>
      </div>
    </ContentGuard>
  );
};

export default TutorialAdmin;
